import type { ChainKey, ChainFamily, ChainCatalogEntry } from './chains';

import { familiesOf, CHAIN_CATALOG, resolveDefaultFamily } from './chains';

/**
 * The chains this build carries, resolved from `VITE_ENABLED_CHAINS` at build time
 * (`vite.config.ts` → `__ENABLED_CHAINS__`).
 *
 * "Runtime multichain" (spec §4.6) means several chains are live at once and the user picks one —
 * not that the set can change without a rebuild. The set stays a build constant so the bundler can
 * still drop the wallet stack of a family this deployment does not use: that is the whole reason
 * the reduced `VITE_ENABLED_CHAINS=tron` build carries no EVM chunk, and why the TON scaffold —
 * referenced from nowhere — is never emitted at all ([[chain-scaffold-must-be-lazy]]).
 *
 * The build-time booleans (`__TRON_ENABLED__`, `__EVM_ENABLED__`) are read *literally* at the few
 * places that guard a dynamic import. Do not replace them with `isFamilyEnabled(...)` there: only
 * a literal `true`/`false` lets the branch — and the chunk behind it — disappear.
 */
export const ENABLED_CHAIN_KEYS: ChainKey[] = __ENABLED_CHAINS__;

export const ENABLED_CHAINS: ChainCatalogEntry[] = ENABLED_CHAIN_KEYS.map(
  (chainKey) => CHAIN_CATALOG[chainKey]
);

export const ENABLED_FAMILIES: ChainFamily[] = familiesOf(ENABLED_CHAIN_KEYS);

/** Family assumed by screens that do not (yet) know the chain of their deal. */
export const DEFAULT_FAMILY: ChainFamily = resolveDefaultFamily(ENABLED_CHAIN_KEYS);

export const isFamilyEnabled = (family: ChainFamily): boolean => ENABLED_FAMILIES.includes(family);

export const isChainEnabled = (chainKey: ChainKey): boolean =>
  ENABLED_CHAIN_KEYS.includes(chainKey);

/** Enabled chains of one family — e.g. every EVM chain the wagmi config has to cover. */
export const enabledChainsOfFamily = (family: ChainFamily): ChainCatalogEntry[] =>
  ENABLED_CHAINS.filter((chain) => chain.family === family);
